/**
 * FFE Simple Direct Guidance - No Mappings, No Complexity  
 * Every requirement ID maps directly to its guidance
 * Based on Regulations Assistant verified guidance
 */

export const FFE_SIMPLE_GUIDANCE = {
  // NEW RA-VERIFIED GUIDANCE (Priority Items 1-10)
  
  // 1. Fire Extinguisher - Outboard ≤220kW
  'ffe.extinguisher.outboard.le220kw': {
    id: 'ffe.extinguisher.outboard.le220kw',
    name: 'Fire Extinguisher - Outboard ≤220kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible for immediate use',
        'Protected from weather and corrosion',
        'Located where outboard engine fire can be safely attacked',
        'Positioned to avoid engine heat and vibration damage'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguisher rated 34B minimum',
      standards: 'BS EN 3 certified marine fire extinguisher (WBC3 Table 16.1.1.1)',
      trigger: 'Outboard engines with aggregate power ≤220kW',
      quantity: 'One 34B rated extinguisher minimum',
      rating: '34B minimum fire rating for Class B (liquid fuel) fires',
      color_requirement: 'Red color per BS EN 3 standard (no repainting to old colors)'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Replace when pressure drops or damage occurs',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security monthly',
        'Verify accessibility is not blocked',
        'Inspect discharge hose and nozzle',
        'Ensure operating instructions are legible'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  // 2. Fire Extinguisher - Outboard >220kW
  'ffe.extinguisher.outboard.gt220kw': {
    id: 'ffe.extinguisher.outboard.gt220kw',
    name: 'Fire Extinguisher - Outboard >220kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Multiple extinguishers distributed for effective coverage',
        'Readily accessible for immediate use from different positions',
        'Protected from weather and engine heat',
        'Positioned to allow safe approach during engine fire'
      ]
    },
    
    specifications: {
      type: 'Multiple portable fire extinguishers with total B rating = 0.3P',
      standards: 'BS EN 3 certified marine fire extinguishers (WBC3 Table 16.1.1.1)',
      trigger: 'Outboard engines with aggregate power >220kW',
      calculation: 'Total B capacity = 0.3 × P (where P = aggregate power in kW)',
      example: 'For 300kW: Total B capacity = 0.3 × 300 = 90B minimum',
      distribution: 'May use multiple extinguishers to achieve total B rating'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of all extinguishers',
        'Monthly visual inspection of pressure and condition',
        'Maintain total B rating by replacing expired units',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check all mounting security monthly',
        'Verify accessibility from engine operating positions',
        'Inspect all discharge equipment and instructions',
        'Consider distribution for optimal fire attack angles'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  // 3. Fire Extinguisher - Inboard Diesel <120kW
  'ffe.extinguisher.inboard.lt120kw': {
    id: 'ffe.extinguisher.inboard.lt120kw',
    name: 'Fire Extinguisher - Inboard Diesel <120kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located adjacent to main entrance of each machinery space (WBC3 Table 16.1.1.1)',
        'Readily accessible from outside machinery space',
        'Protected from machinery space heat and contamination',
        'Clearly visible and marked for emergency use'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose fire extinguisher of appropriate size',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 Table 16.1.1.1)',
      trigger: 'Inboard diesel engines with power <120kW',
      quantity: 'One extinguisher per machinery space entrance',
      multi_purpose: 'Suitable for Class A (solid materials) and Class B (liquid fuels)',
      size_guidance: 'Appropriate size for machinery space volume and fire risk'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify location remains adjacent to machinery entrance',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security and accessibility monthly',
        'Ensure machinery space entrance remains clear',
        'Verify extinguisher type suitable for machinery fires',
        'Test from normal operating positions'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  // 4. Fire Protection - Diesel Engine Box Above Deck
  'ffe.extinguisher.enginebox.above': {
    id: 'ffe.extinguisher.enginebox.above',
    name: 'Fire Protection - Diesel Engine Box Above Deck',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Fire port positioned for safe extinguisher discharge (WBC3 16.1.2.1)',
        'Fire port constructed of fire-resistant material (WBC3 16.1.2.2)',
        'Appropriately sized for discharge nozzle (WBC3 16.1.2.2)',
        'Labeled with "fire port" or graphical symbol (WBC3 16.1.2.2)'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguisher(s) OR fixed fire extinguishing system',
      standards: 'Fire port system per WBC3 16.1.2 or fixed system per WBC3 16.4',
      trigger: 'Diesel engines located in engine box above deck',
      options: [
        'Portable extinguisher with fire port access',
        'Fixed fire extinguishing system per WBC3 16.4'
      ],
      fire_port: 'Must allow discharge without entering space (WBC3 16.1.2.1)',
      alternative: 'Fixed system is acceptable alternative to portable/fire port'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of fire port construction and access',
        'Verify fire port labeling remains clear',
        'If portable: maintain extinguisher per standard requirements',
        'If fixed system: annual service per manufacturer'
      ],
      recommended: [
        'Monthly check fire port accessibility and operation',
        'Verify discharge nozzle fits fire port opening',
        'Test fire port opening mechanism',
        'Ensure crew familiar with fire port operation'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1, 16.1.2'
  },

  // 5. Fixed Fire Extinguishing System
  'ffe.fixed.system.primary': {
    id: 'ffe.fixed.system.primary',
    name: 'Fixed Fire Extinguishing System',
    equipment_type: 'fixed_fire_system',
    
    placement: {
      requirements: [
        'Installed in machinery space per WBC3 16.4',
        'Administration approved type appropriate to space (WBC3 16.4.1.1)',
        'Protected from mechanical damage',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire extinguishing system for machinery space',
      standards: 'Administration approved per WBC3 16.4.1.1',
      trigger: 'Required for engines below deck; optional for engines above deck',
      compliance: [
        'Merchant Shipping (Fire Protection – Small Ships) Regulations 1998 (SI 1998 No. 1011)',
        'MSN 1666 installation requirements'
      ],
      system_types: ['CO2 flooding', 'Dry powder', 'Foam systems', 'Water mist'],
      installation: 'Per manufacturer installation requirements (WBC3 16.4.1.2)'
    },
    
    maintenance: {
      mandatory: [
        'Annual service minimum or per manufacturer (WBC3 16.4.1.2)',
        'Meet manufacturer installation and maintenance requirements',
        'Annual inspection by competent service provider',
        'Pressure testing and discharge testing as required'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check control panel indications and alarms',
        'Verify automatic activation systems',
        'Test manual release mechanisms'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1, 16.4, MSN 1666, SI 1998 No. 1011'
  },

  // 6. Fire Extinguisher - Basic Accommodation
  'ffe.extinguisher.accommodation.basic': {
    id: 'ffe.extinguisher.accommodation.basic',
    name: 'Fire Extinguisher - Basic Accommodation',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located within each accommodation space',
        'Readily accessible for emergency use',
        'Protected from damage and weather',
        'Positioned for safe evacuation route access'
      ]
    },
    
    specifications: {
      type: '5A/34B rated portable fire extinguisher minimum',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 16.2.1.2)',
      trigger: 'Accommodation spaces without sleeping, cooking, or heating',
      rating: '5A rating for solid materials, 34B rating for liquid fuels',
      quantity: 'Minimum one extinguisher per accommodation space',
      multi_purpose: 'Suitable for typical accommodation fire risks'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify accessibility within accommodation space',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security within accommodation',
        'Ensure crew familiar with location and operation',
        'Verify evacuation routes remain clear',
        'Test from normal accommodation positions'
      ]
    },
    
    authority: 'WBC3 16.2.1.2'
  },

  // 7. Fire Extinguisher - Sleeping Only
  'ffe.extinguisher.accommodation.sleeping.only': {
    id: 'ffe.extinguisher.accommodation.sleeping.only',
    name: 'Fire Extinguisher - Sleeping Only',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located within sleeping accommodation space',
        'Readily accessible for emergency evacuation',
        'Positioned near escape route for immediate access',
        'Protected from damage during normal sleeping activities'
      ]
    },
    
    specifications: {
      type: '5A/34B rated portable fire extinguisher minimum',
      standards: 'BS EN 3 certified multi-purpose extinguisher (WBC3 16.2.1.2)',
      trigger: 'Accommodation with sleeping but no cooking or heating appliances',
      rating: '5A rating for bedding/furnishing fires, 34B for electrical equipment',
      quantity: 'Minimum one extinguisher per sleeping space',
      accessibility: 'Critical for night-time emergency response'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify accessibility from sleeping areas',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security near sleeping areas',
        'Ensure crew familiar with night-time location',
        'Verify no obstruction by sleeping arrangements',
        'Practice emergency access in low light conditions'
      ]
    },
    
    authority: 'WBC3 16.2.1.2'
  },

  // 8. Fire Protection - No Open Flame
  'ffe.extinguisher.cooking.noflame': {
    id: 'ffe.extinguisher.cooking.noflame',
    name: 'Fire Protection - No Open Flame',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located near cooking or heating appliance',
        'Readily accessible during cooking/heating operations',
        'Protected from heat and cooking vapors',
        'Positioned for safe use during appliance fire'
      ]
    },
    
    specifications: {
      type: '5A/34B rated portable fire extinguisher OR fixed system',
      standards: 'BS EN 3 extinguisher or fixed system per WBC3 16.4',
      trigger: 'Cooking or heating appliances without open flame (electric/induction)',
      options: [
        '5A/34B rated portable fire extinguisher',
        'Fixed fire extinguishing system per WBC3 16.4'
      ],
      rating: '5A for solid materials, 34B for electrical fires',
      application: 'Electric or induction cooking/heating equipment'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection if portable extinguisher',
        'Annual service if fixed system per manufacturer',
        'Verify continued suitability for appliance type'
      ],
      recommended: [
        'Check accessibility during normal cooking operations',
        'Ensure crew trained on electrical fire suppression',
        'Verify extinguisher type suitable for electrical equipment',
        'Test from normal cooking positions'
      ]
    },
    
    authority: 'WBC3 Table 16.2.3.1, 16.4'
  },

  // 9. Fire Protection - Open Flame
  'ffe.extinguisher.cooking.flame': {
    id: 'ffe.extinguisher.cooking.flame',
    name: 'Fire Protection - Open Flame',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Located near open flame cooking/heating appliance',
        'Both extinguisher and blanket readily accessible',
        'Protected from heat but immediately available',
        'Positioned for safe use during flame emergencies'
      ]
    },
    
    specifications: {
      type: 'One 5A/34B extinguisher AND (one 8A/68B extinguisher OR fire blanket) OR fixed system',
      standards: 'BS EN 3 extinguishers and approved fire blanket or WBC3 16.4 fixed system',
      trigger: 'Cooking or heating appliances with open flame (gas, alcohol, solid fuel)',
      minimum_protection: [
        'One 5A/34B rated portable fire extinguisher (always required)',
        'PLUS either: One 8A/68B rated extinguisher OR fire blanket'
      ],
      alternative: 'Fixed fire extinguishing system per WBC3 16.4',
      enhanced_protection: 'Higher rating required due to open flame risk'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of all fire protection equipment',
        'Monthly visual inspection of extinguishers and blanket',
        'Verify fire blanket not damaged or contaminated',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check accessibility during cooking operations',
        'Ensure crew trained on open flame fire suppression',
        'Verify fire blanket deployment mechanism',
        'Practice emergency shutdown of appliances'
      ]
    },
    
    authority: 'WBC3 Table 16.2.3.1, 16.4'
  },

  // 10. Open Boat ≤8m Protection
  'ffe.extinguisher.openboat.le8m': {
    id: 'ffe.extinguisher.openboat.le8m',
    name: 'Open Boat ≤8m Protection',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Two extinguishers distributed for accessibility',
        'Protected from weather and sea spray',
        'Secured against vessel motion but readily accessible',
        'Positioned for use from different locations on deck'
      ]
    },
    
    specifications: {
      type: 'Minimum two 34B rated portable fire extinguishers',
      standards: 'BS EN 3 certified marine fire extinguishers (WBC3 Table 16.1.1.1 Note d)',
      trigger: 'Open boats <8m length without substantial enclosure and no cooking',
      quantity: 'Minimum two 34B rated extinguishers required',
      distribution: 'Two separate extinguishers for redundancy and accessibility',
      exemptions: 'Not required if cooking appliances present (different requirements apply)'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of both extinguishers',
        'Monthly visual inspection of pressure and weather protection',
        'Verify both extinguishers remain accessible',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security against sea conditions',
        'Ensure weather protection remains effective',
        'Verify crew can access from normal operating positions',
        'Test accessibility during typical sea conditions'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1 Note d'
  },

  // 11. Fire Pump
  'ffe.fire.pump.required': {
    id: 'ffe.fire.pump.required',
    name: 'Fire Pump',
    equipment_type: 'fire_pump',
    
    placement: {
      requirements: [
        'Power pump: ensures fire main pressure after machinery space loss (WBC3 16.4.2.1)',
        'Hand pump: located outside machinery space (WBC3 16.4.2.1)',
        'Sea and hose connections for water jet delivery (WBC3 16.4.2.4)',
        'Accessible changeover arrangement if using bilge pump (WBC3 16.4.2.5)'
      ]
    },
    
    specifications: {
      type: 'Power driven self-priming fire pump OR hand fire pump',
      standards: 'Fire pump per WBC3 16.4.2.1',
      trigger: 'All vessels except open boats <8m without substantial enclosure',
      options: [
        'Power driven self-priming fire pump (maintains pressure after machinery space loss)',
        'Hand fire pump outside machinery space (where power pump impracticable)'
      ],
      exemption: 'Open boats <8m length without substantial enclosure (WBC3 16.4.2.3)',
      dual_use: 'Bilge pump may serve as fire pump with changeover (WBC3 16.4.2.5)',
      delivery: 'One jet of water to any part of ship through hose and nozzle (WBC3 16.4.2.4)'
    },
    
    maintenance: {
      mandatory: [
        'Annual pump testing and inspection',
        'Verify sea connection integrity and flow',
        'Test changeover arrangement if dual-use pump',
        'Ensure pump accessibility and operation'
      ],
      recommended: [
        'Monthly pump operation test',
        'Check suction strainer condition',
        'Verify pump priming system operation',
        'Test delivery pressure and flow rate'
      ]
    },
    
    authority: 'WBC3 16.4.2.1, 16.4.2.3, 16.4.2.4, 16.4.2.5'
  },

  // 12. Fire Hose
  'ffe.fire.hose.fitted': {
    id: 'ffe.fire.hose.fitted',
    name: 'Fire Hose',
    equipment_type: 'fire_hose',
    
    placement: {
      requirements: [
        'Connected to fire pump with sea and hose connections',
        'Adequate length to reach any part of vessel',
        'Protected from damage and weather when stowed',
        'Readily accessible for emergency deployment'
      ]
    },
    
    specifications: {
      type: 'Fire hose of adequate length with 10mm nozzle and spray nozzle',
      standards: 'Fire hose per WBC3 16.4.2.4',
      trigger: 'Required when vessel has fire pump',
      length: 'Adequate length to reach any part of ship',
      nozzles: [
        '10mm nozzle (WBC3 16.4.2.4)',
        'Suitable spray nozzle (WBC3 16.4.2.4)'
      ],
      delivery: 'Capable of delivering one jet of water to any vessel location',
      material: 'Marine-grade fire hose suitable for seawater service'
    },
    
    maintenance: {
      mandatory: [
        'Annual hose pressure testing and inspection',
        'Check nozzle operation and spray patterns',
        'Verify hose length adequacy for vessel coverage',
        'Test hose connections and couplings'
      ],
      recommended: [
        'Monthly visual inspection for damage or deterioration',
        'Check hose stowage and accessibility',
        'Test nozzle adjustment and spray control',
        'Verify hose remains supple and kink-free'
      ]
    },
    
    authority: 'WBC3 16.4.2.4'
  },

  // 13. Fire Buckets
  'ffe.fire.buckets.fitted': {
    id: 'ffe.fire.buckets.fitted',
    name: 'Fire Buckets',
    equipment_type: 'fire_buckets',
    
    placement: {
      requirements: [
        'Readily accessible for emergency use',
        'Protected from weather but immediately available',
        'Distributed for coverage of vessel areas',
        'Secured against vessel motion with lanyards'
      ]
    },
    
    specifications: {
      type: 'Two buckets with lanyards minimum',
      standards: 'Fire buckets per WBC3 16.6.1',
      trigger: 'Where practicable or deemed necessary by Certifying Authority',
      quantity: 'Minimum two buckets with lanyards',
      materials: 'Metal, plastic or canvas suitable for marine service',
      exemptions: [
        'Not required where multiple extinguishers present',
        'Not required where fixed fire system installed'
      ],
      discretion: 'Certifying Authority determines necessity based on vessel configuration'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of bucket condition and integrity',
        'Check lanyard attachment and condition',
        'Verify bucket accessibility and security',
        'Replace damaged or deteriorated buckets'
      ],
      recommended: [
        'Monthly check bucket stowage and accessibility',
        'Verify lanyards prevent loss overboard',
        'Check buckets free from contamination',
        'Ensure crew familiar with bucket locations'
      ]
    },
    
    authority: 'WBC3 16.6.1, Note 65'
  },

  // 14. Fire Blanket
  'ffe.fire.blanket': {
    id: 'ffe.fire.blanket',
    name: 'Fire Blanket',
    equipment_type: 'fire_blanket',
    
    placement: {
      requirements: [
        'Located in area with fire risk (cooking/heating with open flame)',
        'Readily accessible during cooking or heating operations',
        'Protected from contamination but immediately deployable',
        'Positioned for safe use during appliance emergencies'
      ]
    },
    
    specifications: {
      type: 'Fire blanket meeting appropriate standard',
      standards: 'BS EN 1869:2019 Fire Blankets (MIN 698)',
      trigger: [
        'Cooking or heating appliances with open flame',
        'Any area identified by CA as increased fire risk'
      ],
      locations: 'Galley or cooking area where fire risk identified',
      alternative: 'May be used instead of 8A/68B extinguisher for open flame protection',
      deployment: 'Must be readily deployable for smothering fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection by competent person',
        'Check blanket material integrity and fire resistance',
        'Verify deployment mechanism operation',
        'Replace if damaged or contaminated'
      ],
      recommended: [
        'Monthly visual inspection for damage or contamination',
        'Check accessibility during normal cooking operations',
        'Verify blanket deployment instructions remain clear',
        'Ensure crew trained on proper deployment technique'
      ]
    },
    
    authority: 'WBC3 16.5, 16.5.2, MIN 698'
  },

  // 15. Fire Detection - Machinery Space
  'ffe.detector.machinery': {
    id: 'ffe.detector.machinery',
    name: 'Fire Detection - Machinery Space',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in machinery space(s) (WBC3 15.6.2.1)',
        'Positioned per EN 54 standard requirements',
        'Audible from space concerned, accommodation, and control position (WBC3 15.6.2.3)',
        'Capable of being heard with doors closed and machinery running'
      ]
    },
    
    specifications: {
      type: 'Fire detectors detecting smoke and heat, OR flame detectors',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'All vessels with machinery space(s)',
      detection_options: [
        'Smoke and heat detectors (combined requirement)',
        'Flame detectors (alternative to smoke/heat)'
      ],
      alarm_requirements: [
        'Audible from space concerned (WBC3 15.6.2.3)',
        'Audible from accommodation spaces (WBC3 15.6.2.3)',
        'Audible from control position(s) (WBC3 15.6.2.3)',
        'Audible with doors closed and machinery running'
      ]
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Verify alarm audibility in all required locations',
        'Test detection response to appropriate hazards'
      ],
      recommended: [
        'Monthly functional testing of detection and alarms',
        'Check detector cleanliness and condition',
        'Verify alarm continues to be audible in operational conditions',
        'Test from different machinery space locations'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.4, 15.6.2.3, EN 54'
  },

  // 16. Fire Detector - Accommodation
  'ffe.detector.accommodation': {
    id: 'ffe.detector.accommodation',
    name: 'Fire Detector - Accommodation',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in accommodation spaces (WBC3 15.6.2.1)',
        'Appropriate type for accommodation hazards (WBC3 15.6.2.2)',
        'Audible from accommodation, control position, and concerned spaces',
        'Positioned per EN 54 installation requirements'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for accommodation space hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'All vessels with accommodation spaces',
      detector_selection: 'Appropriate type to respond to potential hazards (smoke, flame)',
      coverage: 'All accommodation spaces require detection',
      alarm_requirements: [
        'Audible from accommodation spaces (WBC3 15.6.2.3)',
        'Audible from control position(s) (WBC3 15.6.2.3)',
        'Audible from space concerned (WBC3 15.6.2.3)'
      ]
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Verify alarm audibility in all accommodation areas',
        'Test detection response appropriate for accommodation hazards'
      ],
      recommended: [
        'Monthly functional testing of detectors and alarms',
        'Check detector positioning remains optimal for accommodation layout',
        'Verify alarms wake sleeping occupants',
        'Test alarm audibility with doors closed'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  // 17. Fire Detector - Open Flame Spaces
  'ffe.detector.openflame.spaces': {
    id: 'ffe.detector.openflame.spaces',
    name: 'Fire Detector - Open Flame Spaces',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in spaces with open flame, gas, or liquid fuel appliances (WBC3 15.6.2.1)',
        'Appropriate type for open flame and fuel hazards (WBC3 15.6.2.2)',
        'Positioned to detect fires from appliances and fuel systems',
        'Audible from all required locations per WBC3 15.6.2.3'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for open flame and fuel hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'Spaces containing open flame, gas, or liquid fuel appliances',
      appliance_types: [
        'Open flame cooking or heating appliances',
        'Gas appliances (LPG, natural gas)',
        'Liquid fuel appliances (alcohol, diesel heating)'
      ],
      detector_selection: 'Type appropriate for flame, gas, and fuel fire hazards',
      alarm_coverage: 'Audible from space, accommodation, and control positions'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection considering appliance fire risks',
        'Verify detection appropriate for specific appliance hazards',
        'Test alarm audibility in all operational conditions'
      ],
      recommended: [
        'Monthly functional testing during appliance operations',
        'Check detector positioning relative to appliances',
        'Verify detection covers all appliance operating areas',
        'Test alarm response during normal appliance use'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  // 18. Fire Detector - Electrical Areas
  'ffe.detector.electrical.concentration': {
    id: 'ffe.detector.electrical.concentration',
    name: 'Fire Detector - Electrical Areas',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in areas of concentrated electrical equipment (WBC3 15.6.2.1)',
        'Appropriate type for electrical fire hazards (WBC3 15.6.2.2)',
        'Positioned to detect fires from electrical equipment and wiring',
        'Audible from electrical areas, accommodation, and control positions'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for electrical equipment hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'Areas with concentrated electrical equipment',
      electrical_areas: [
        'Main electrical switchboards',
        'Distribution panels and control rooms',
        'Battery compartments with electrical systems',
        'Areas with concentrated wiring or electrical installations'
      ],
      detector_selection: 'Type appropriate for electrical fires (smoke detection typically)',
      alarm_requirements: 'Audible per WBC3 15.6.2.3 requirements'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection considering electrical fire risks',
        'Verify detection appropriate for electrical equipment present',
        'Coordinate with electrical system maintenance schedules'
      ],
      recommended: [
        'Monthly functional testing in electrical areas',
        'Check detector positioning covers all electrical concentrations',
        'Verify detection sensitivity appropriate for electrical fires',
        'Coordinate testing with electrical equipment operations'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  // 19. CO Detector - Accommodation
  'ffe.detector.co.accommodation': {
    id: 'ffe.detector.co.accommodation',
    name: 'CO Detector - Accommodation',
    equipment_type: 'co_detector',
    
    placement: {
      requirements: [
        'Installed per manufacturer instructions (WBC3 15.6.3.1)',
        'Located in accommodation areas near open flame/gas/liquid fuel appliances',
        'Positioned to detect CO accumulation in accommodation spaces',
        'Consider CO source locations and air circulation patterns'
      ]
    },
    
    specifications: {
      type: 'Carbon monoxide detector(s) for accommodation areas',
      standards: 'CO detector per manufacturer instructions (WBC3 15.6.3.1)',
      trigger: 'Open flame, gas, or liquid fuel appliances in/adjacent to accommodation',
      appliance_sources: [
        'Open flame cooking or heating in/near accommodation',
        'Gas appliances adjacent to accommodation areas',
        'Liquid fuel appliances near accommodation spaces'
      ],
      exemption: 'Not required for electric-only appliances',
      installation: 'Per manufacturer instructions for optimal CO detection'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Battery replacement per manufacturer schedule',
        'Verify continued operation and alarm function'
      ],
      recommended: [
        'Monthly functional test using CO test gas or button',
        'Check battery level indicators regularly',
        'Verify detector positioning remains optimal',
        'Clean detector sensing elements per manufacturer instructions'
      ]
    },
    
    authority: 'WBC3 15.6.3.1, 15.6.1.1'
  },

  // 20. CO Detector - Exhaust Risk Areas
  'ffe.detector.co.exhaust': {
    id: 'ffe.detector.co.exhaust',
    name: 'CO Detector - Exhaust Risk Areas',
    equipment_type: 'co_detector',
    
    placement: {
      requirements: [
        'Installed in spaces where exhaust gases may accumulate (WBC3 15.6.3.3)',
        'Positioned to detect CO from potential exhaust leaks',
        'Consider exhaust system routing and potential leak points',
        'Located where exhaust gases could accumulate due to ventilation patterns'
      ]
    },
    
    specifications: {
      type: 'Carbon monoxide detector(s) for exhaust leak protection',
      standards: 'CO detector per WBC3 15.6.3.3',
      trigger: 'Spaces with exhaust gas accumulation risk from leaks',
      risk_areas: [
        'Accommodation spaces adjacent to exhaust systems',
        'Enclosed spaces near exhaust piping',
        'Areas with poor ventilation where exhaust could accumulate',
        'Spaces below exhaust outlets where gases could collect'
      ],
      purpose: 'Detect CO accumulation from exhaust system leaks',
      location_assessment: 'Consider exhaust routing and ventilation characteristics'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection considering exhaust system layout',
        'Verify detector positioning covers potential accumulation areas',
        'Coordinate with exhaust system maintenance and inspection'
      ],
      recommended: [
        'Monthly functional testing in exhaust risk areas',
        'Check exhaust system integrity during detector testing',
        'Verify ventilation effectiveness in protected spaces',
        'Review detector placement if exhaust system modifications made'
      ]
    },
    
    authority: 'WBC3 15.6.3.3, 15.6.1.1'
  },

  // 21. Battery Fire Detection
  'ffe.detector.battery.fire': {
    id: 'ffe.detector.battery.fire',
    name: 'Battery Fire Detection',
    equipment_type: 'battery_fire_detection',
    
    placement: {
      requirements: [
        'Fitted in battery rooms, battery boxes, or spaces containing battery boxes (WBC3 Annex 1 3.2.1)',
        'Positioned to detect fires from battery cells and connections',
        'Auditory and visual alarms required (WBC3 Annex 1 3.2.1)',
        'Coverage of all battery installation areas'
      ]
    },
    
    specifications: {
      type: 'Smoke, heat, and flame detectors (all three types required)',
      standards: 'Suitable detectors per WBC3 Annex 1 Section 3.2.1',
      trigger: 'Battery electric or hybrid propulsion vessels',
      detection_types: [
        'Smoke detectors (required)',
        'Heat detectors (required)', 
        'Flame detectors (required)'
      ],
      alarm_requirements: [
        'Auditory alarms (WBC3 Annex 1 3.2.1)',
        'Visual alarms (WBC3 Annex 1 3.2.1)'
      ],
      coverage: 'Battery rooms, battery boxes, and spaces containing battery boxes'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Verify all three detection types (smoke, heat, flame) operational',
        'Test auditory and visual alarm functions',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional testing of detection systems',
        'Check detector positioning covers all battery areas',
        'Verify alarm audibility and visibility during battery operations',
        'Coordinate testing with battery system maintenance'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.1'
  },

  // 22. Battery Gas Detection
  'ffe.detector.battery.gas': {
    id: 'ffe.detector.battery.gas',
    name: 'Battery Gas Detection',
    equipment_type: 'battery_gas_detection',
    
    placement: {
      requirements: [
        'Fitted where risk assessment determines gas accumulation possible (WBC3 Annex 1 3.2.2)',
        'Located in battery rooms and enclosed battery spaces',
        'Positioned to detect explosive gas accumulation near batteries',
        'Consider battery ventilation patterns and gas collection points'
      ]
    },
    
    specifications: {
      type: 'Gas detectors for explosive gas detection',
      standards: 'Gas detectors per WBC3 Annex 1 Section 3.2.2',
      trigger: 'Risk assessment determines explosive gas accumulation possible up to LEL',
      detection_threshold: '60% LEL automatic disconnect required (WBC3 Annex 1 3.2.2)',
      automatic_action: 'Disconnect battery system at 60% LEL',
      risk_based: 'Required only where risk assessment identifies LEL accumulation risk',
      gas_types: 'Hydrogen and other explosive gases from battery systems'
    },
    
    maintenance: {
      mandatory: [
        'Annual calibration and testing by competent person',
        'Verify 60% LEL automatic disconnect function',
        'Test detector response to target gases',
        'Review risk assessment periodically for continued applicability'
      ],
      recommended: [
        'Monthly functional testing with test gas',
        'Check detector sensitivity and calibration status',
        'Verify automatic disconnect operates correctly',
        'Monitor battery room ventilation effectiveness'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.2'
  },

  // 23. Fixed Fire Suppression - Battery
  'ffe.fixed.system.battery': {
    id: 'ffe.fixed.system.battery',
    name: 'Fixed Fire Suppression - Battery',
    equipment_type: 'battery_fire_suppression',
    
    placement: {
      requirements: [
        'Installed in battery boxes and battery rooms (WBC3 Annex 1 3.4.1)',
        'Per battery manufacturer installation requirements',
        'Protected from mechanical damage',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire suppression system for battery spaces',
      standards: 'Per battery manufacturer requirements (WBC3 Annex 1 3.4.1)',
      trigger: 'All battery electric and hybrid propulsion vessels (mandatory)',
      manufacturer_compliance: 'Must accord with battery manufacturer requirements',
      coverage: 'Battery boxes and battery rooms',
      system_types: ['High pressure water mist (recommended)', 'Foam systems', 'Other manufacturer-approved systems'],
      installation: 'Per manufacturer installation requirements and WBC3 Annex 1'
    },
    
    maintenance: {
      mandatory: [
        'Service per manufacturer requirements (WBC3 Annex 1 3.4.1)',
        'Annual inspection by competent service provider',
        'Verify system compatibility with battery type',
        'Test automatic activation systems'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check control panel indications and alarms',
        'Verify manual activation mechanisms',
        'Coordinate with battery system maintenance schedules'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.4.1'
  },

  // 24. Battery Fire Extinguisher
  'ffe.extinguisher.battery': {
    id: 'ffe.extinguisher.battery',
    name: 'Battery Fire Extinguisher',
    equipment_type: 'battery_fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible to battery spaces (WBC3 Annex 1 3.5.2)',
        'Protected from weather and contamination',
        'Located for safe approach during battery fire',
        'Positioned near battery room/box entrances'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguishers suitable for battery fires',
      standards: 'Minimum 34B rating per WBC3 Annex 1 3.5.2',
      trigger: 'Battery electric and hybrid propulsion vessels',
      quantity: 'Minimum two extinguishers with 34B rating',
      additional_requirement: 'In addition to Section 16 fire protection requirements',
      manufacturer_type: 'Per battery manufacturer type requirements (WBC3 Annex 1 3.5.2)',
      rating: 'Minimum 34B fire rating for each extinguisher'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify extinguisher type suitable for battery fires',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security and accessibility monthly',
        'Ensure crew trained on battery fire suppression techniques',
        'Verify extinguisher compatibility with battery type',
        'Test accessibility during normal battery operations'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.5.2'
  },

  // 25. Hydrocarbon Gas Detector
  'ffe.detector.gas.hydrocarbon': {
    id: 'ffe.detector.gas.hydrocarbon',
    name: 'Hydrocarbon Gas Detector',
    equipment_type: 'gas_detector',
    
    placement: {
      requirements: [
        'Fitted in spaces with gas appliances or gas leak risk (WBC3 15.6.4.1)',
        'Positioned to detect gas accumulation patterns',
        'Consider gas density and ventilation characteristics',
        'Located where flammable gas may leak or accumulate'
      ]
    },
    
    specifications: {
      type: 'Gas detector and alarm system for flammable gases',
      standards: 'Suitable gas detector per WBC3 15.6.4.1',
      trigger: 'Spaces with gas appliances or flammable gas leak potential',
      detection: 'Hydrocarbon and flammable gas detection',
      alarm_required: 'Alarm system required with detector (WBC3 15.6.4.1)',
      coverage: 'All spaces where gas may leak or accumulate',
      gas_types: ['LPG', 'Natural gas', 'Other flammable hydrocarbon gases']
    },
    
    maintenance: {
      mandatory: [
        'Annual calibration and testing by competent person',
        'Verify alarm system operation with detector',
        'Test detector response to target gases',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional testing with test gas',
        'Check detector positioning relative to gas appliances',
        'Verify alarm audibility in operational conditions',
        'Monitor gas appliance connections for leak prevention'
      ]
    },
    
    authority: 'WBC3 15.6.4.1'
  },

  // 26. Fire Detection System - Li-ion Battery
  'ffe.detector.lithium.system': {
    id: 'ffe.detector.lithium.system',
    name: 'Fire Detection System - Li-ion Battery',
    equipment_type: 'lithium_fire_detection',
    
    placement: {
      requirements: [
        'Fitted in lithium battery rooms, boxes, and containing spaces (WBC3 Annex 1 3.2.1)',
        'All three detector types required in each space',
        'Positioned for optimal detection of lithium battery fires',
        'Auditory and visual alarms required'
      ]
    },
    
    specifications: {
      type: 'Complete fire detection system with smoke, heat, and flame detectors',
      standards: 'Suitable detectors per WBC3 Annex 1 Section 3.2.1',
      trigger: 'Vessels with lithium-ion battery installations',
      required_detectors: [
        'Smoke detectors (mandatory)',
        'Heat detectors (mandatory)',
        'Flame detectors (mandatory)'
      ],
      alarm_system: 'Auditory and visual alarms for all three detection types',
      coverage: 'All lithium battery installation areas',
      integration: 'May integrate with battery management system'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of all three detector types',
        'Verify smoke, heat, and flame detection functions separately',
        'Test auditory and visual alarm systems',
        'Maintain per manufacturer recommendations for each type'
      ],
      recommended: [
        'Monthly functional testing of complete detection system',
        'Check detector positioning covers all lithium battery areas',
        'Verify alarm integration with battery management system',
        'Test emergency response procedures for different fire types'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.1'
  },

  // 27. Control Position Fire Protection
  'ffe.extinguisher.control.position': {
    id: 'ffe.extinguisher.control.position',
    name: 'Control Position Fire Protection',
    equipment_type: 'control_position_extinguisher',
    
    placement: {
      requirements: [
        'Located within 2m from main control position (WBC3 16.3.1.3.1)',
        'Readily accessible during normal vessel operations',
        'Protected from damage but immediately available',
        'Positioned for safe access during control position emergencies'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose portable fire extinguisher',
      standards: 'Portable fire extinguisher per WBC3 16.3.1.3.1',
      trigger: 'Universal requirement for all seagoing vessels',
      distance_requirement: 'Within 2 metres from main control position',
      quantity: 'One extinguisher minimum within specified distance',
      universal_requirement: 'Required regardless of other engine protection arrangements',
      multi_purpose: 'Suitable for electrical and general fires at control position'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify distance remains within 2m of control position',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check accessibility during normal vessel operations',
        'Ensure crew familiar with control position extinguisher location',
        'Verify extinguisher type suitable for control position equipment',
        'Test access from normal control positions'
      ]
    },
    
    authority: 'WBC3 16.3.1.3.1'
  },

  // 28. Fire Control and Safety Plan
  'ffe.plan.fire.control': {
    id: 'ffe.plan.fire.control',
    name: 'Fire Control and Safety Plan',
    equipment_type: 'fire_control_plan',
    
    placement: {
      requirements: [
        'Prominently displayed at main control position (WBC3 15.8.1)',
        'Readily accessible during emergency situations',
        'Protected from weather but clearly visible',
        'Located where emergency response is coordinated'
      ]
    },
    
    specifications: {
      type: 'Fire control and safety plan showing equipment locations',
      standards: 'Fire control plan per WBC3 15.8.1',
      trigger: 'Universal requirement for all seagoing vessels',
      display_requirement: 'Prominently displayed at main control position',
      content: [
        'Fire equipment locations and types',
        'Fire fighting system arrangements',
        'Escape routes and assembly points',
        'Emergency communication arrangements'
      ],
      accessibility: 'Available for emergency response coordination'
    },
    
    maintenance: {
      mandatory: [
        'Annual review and update if vessel configuration changes',
        'Verify plan remains accurate for current equipment installation',
        'Check display condition and legibility',
        'Update after any fire equipment modifications'
      ],
      recommended: [
        'Monthly check plan display condition and visibility',
        'Ensure crew familiar with fire plan contents',
        'Verify plan accuracy during equipment inspections',
        'Review during emergency drills and training'
      ]
    },
    
    authority: 'WBC3 15.8.1'
  },

  // 29. Escape Route Marking
  'ffe.escape.route.marking': {
    id: 'ffe.escape.route.marking',
    name: 'Escape Route Marking',
    equipment_type: 'escape_route_marking',
    
    placement: {
      requirements: [
        'Clearly marked on both sides of escape route (WBC3 15.7.4)',
        'Visible in normal and emergency lighting conditions',
        'Located at decision points along escape routes',
        'Marked for clear identification of escape purpose'
      ]
    },
    
    specifications: {
      type: 'Escape route marking and signage',
      standards: 'Escape route marking per WBC3 15.7.4',
      trigger: 'Vessels with identified escape routes',
      marking_requirement: 'Both sides of escape route must be marked',
      visibility: 'Clear marking for escape route purpose identification',
      coverage: 'All designated means of escape',
      durability: 'Markings must withstand marine environment'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of all escape route markings',
        'Verify markings remain clear and legible',
        'Check both sides of escape routes are properly marked',
        'Replace damaged or faded markings'
      ],
      recommended: [
        'Monthly visual inspection of escape route marking condition',
        'Verify markings visible in different lighting conditions',
        'Check marking visibility is not obstructed',
        'Review marking effectiveness during emergency drills'
      ]
    },
    
    authority: 'WBC3 15.7.4'
  },

  // 30. Additional Fire Pump - Dangerous Goods
  'ffe.fire.pump.dangerous.goods': {
    id: 'ffe.fire.pump.dangerous.goods',
    name: 'Additional Fire Pump - Dangerous Goods',
    equipment_type: 'additional_fire_pump',
    
    placement: {
      requirements: [
        'Second powered fire pump in addition to WBC3 16.4.2 requirements',
        'Located for effective fire fighting capability',
        'Protected from dangerous goods fire damage',
        'Accessible for maintenance and operation'
      ]
    },
    
    specifications: {
      type: 'Second powered fire pump for dangerous goods vessels',
      standards: 'Powered fire pump per WBC3 29.8.1',
      trigger: 'Vessels carrying dangerous goods (excluding Light Duty certificates)',
      additional_requirement: 'In addition to standard fire pump requirements',
      light_duty_restriction: 'Dangerous goods not permitted on Light Duty certificates',
      pump_type: 'Powered fire pump (not hand pump)',
      capacity: 'Suitable for dangerous goods fire fighting requirements'
    },
    
    maintenance: {
      mandatory: [
        'Annual pump testing and inspection',
        'Verify pump operation and delivery capacity',
        'Test pump accessibility and emergency operation',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Monthly pump operation test',
        'Check pump protection from dangerous goods areas',
        'Verify pump capability for dangerous goods fire scenarios',
        'Coordinate maintenance with primary fire pump schedule'
      ]
    },
    
    authority: 'WBC3 29.8.1'
  },

  // ACTUAL EQUIPMENT IDs FROM EQUIPMENT REGISTER
  
  // Fire Blanket (Galley) - actual ID from register
  'ffe_fire_blanket_galley': {
    id: 'ffe_fire_blanket_galley',
    name: 'Fire Blanket (Galley)',
    equipment_type: 'fire_blanket',
    
    placement: {
      requirements: [
        'Located in galley area with fire risk (cooking/heating with open flame)',
        'Readily accessible during cooking or heating operations',
        'Protected from contamination but immediately deployable',
        'Positioned for safe use during appliance emergencies'
      ]
    },
    
    specifications: {
      type: 'Fire blanket meeting appropriate standard',
      standards: 'BS EN 1869:2019 Fire Blankets (MIN 698)',
      trigger: [
        'Cooking or heating appliances with open flame',
        'Any area identified by CA as increased fire risk'
      ],
      locations: 'Galley or cooking area where fire risk identified',
      alternative: 'May be used instead of 8A/68B extinguisher for open flame protection',
      deployment: 'Must be readily deployable for smothering fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection by competent person',
        'Check blanket material integrity and fire resistance',
        'Verify deployment mechanism operation',
        'Replace if damaged or contaminated'
      ],
      recommended: [
        'Monthly visual inspection for damage or contamination',
        'Check accessibility during normal cooking operations',
        'Verify blanket deployment instructions remain clear',
        'Ensure crew trained on proper deployment technique'
      ]
    },
    
    authority: 'WBC3 16.5, 16.5.2, MIN 698'
  },

  // Battery Equipment - actual IDs from register
  'ffe.battery.fixed_suppression': {
    id: 'ffe.battery.fixed_suppression',
    name: 'Fixed Fire Suppression System (Battery Compartment)',
    equipment_type: 'battery_fire_suppression',
    
    placement: {
      requirements: [
        'Installed in battery boxes and battery rooms (WBC3 Annex 1 3.4.1)',
        'Per battery manufacturer installation requirements',
        'Protected from mechanical damage',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire suppression system for battery spaces',
      standards: 'Per battery manufacturer requirements (WBC3 Annex 1 3.4.1)',
      trigger: 'All battery electric and hybrid propulsion vessels (mandatory)',
      manufacturer_compliance: 'Must accord with battery manufacturer requirements',
      coverage: 'Battery boxes and battery rooms',
      system_types: ['High pressure water mist (recommended)', 'Foam systems', 'Other manufacturer-approved systems']
    },
    
    maintenance: {
      mandatory: [
        'Service per manufacturer requirements (WBC3 Annex 1 3.4.1)',
        'Annual inspection by competent service provider',
        'Verify system compatibility with battery type',
        'Test automatic activation systems'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check control panel indications and alarms',
        'Verify manual activation mechanisms',
        'Coordinate with battery system maintenance schedules'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.4.1'
  },

  'ffe.battery.fire_detection': {
    id: 'ffe.battery.fire_detection',
    name: 'Fire Detection System (Battery Compartment)',
    equipment_type: 'battery_fire_detection',
    
    placement: {
      requirements: [
        'Fitted in battery rooms, battery boxes, or spaces containing battery boxes (WBC3 Annex 1 3.2.1)',
        'Positioned to detect fires from battery cells and connections',
        'Auditory and visual alarms required (WBC3 Annex 1 3.2.1)',
        'Coverage of all battery installation areas'
      ]
    },
    
    specifications: {
      type: 'Smoke, heat, and flame detectors (all three types required)',
      standards: 'Suitable detectors per WBC3 Annex 1 Section 3.2.1',
      trigger: 'Battery electric or hybrid propulsion vessels',
      detection_types: [
        'Smoke detectors (required)',
        'Heat detectors (required)', 
        'Flame detectors (required)'
      ],
      alarm_requirements: [
        'Auditory alarms (WBC3 Annex 1 3.2.1)',
        'Visual alarms (WBC3 Annex 1 3.2.1)'
      ],
      coverage: 'Battery rooms, battery boxes, and spaces containing battery boxes'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Verify all three detection types (smoke, heat, flame) operational',
        'Test auditory and visual alarm functions',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional testing of detection systems',
        'Check detector positioning covers all battery areas',
        'Verify alarm audibility and visibility during battery operations',
        'Coordinate testing with battery system maintenance'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.1'
  },

  'ffe.battery.portable_extinguishers': {
    id: 'ffe.battery.portable_extinguishers',
    name: 'Portable Fire Extinguisher - 2x 34B (Battery Area)',
    equipment_type: 'battery_fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible to battery spaces (WBC3 Annex 1 3.5.2)',
        'Protected from weather and contamination',
        'Located for safe approach during battery fire',
        'Positioned near battery room/box entrances'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguishers suitable for battery fires',
      standards: 'Minimum 34B rating per WBC3 Annex 1 3.5.2',
      trigger: 'Battery electric and hybrid propulsion vessels',
      quantity: 'Minimum two extinguishers with 34B rating',
      additional_requirement: 'In addition to Section 16 fire protection requirements',
      manufacturer_type: 'Per battery manufacturer type requirements (WBC3 Annex 1 3.5.2)',
      rating: 'Minimum 34B fire rating for each extinguisher'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify extinguisher type suitable for battery fires',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security and accessibility monthly',
        'Ensure crew trained on battery fire suppression techniques',
        'Verify extinguisher compatibility with battery type',
        'Test accessibility during normal battery operations'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.5.2'
  },

  'ffe.battery.thermal_runaway_protection': {
    id: 'ffe.battery.thermal_runaway_protection',
    name: 'Thermal Runaway Protection (Battery System)',
    equipment_type: 'battery_thermal_protection',
    
    placement: {
      requirements: [
        'Integrated with battery management system',
        'Monitoring all battery cells and modules',
        'Connected to fire suppression and alarm systems',
        'Accessible for maintenance and testing'
      ]
    },
    
    specifications: {
      type: 'Thermal runaway detection and protection system',
      standards: 'Per battery manufacturer requirements (WBC3 Annex 1)',
      trigger: 'Battery electric and hybrid propulsion vessels with lithium batteries',
      monitoring: 'Continuous temperature and thermal gradient monitoring',
      response: 'Automatic system isolation and fire suppression activation',
      integration: 'Must integrate with battery management system and fire protection'
    },
    
    maintenance: {
      mandatory: [
        'Annual testing and calibration by competent person',
        'Verify thermal monitoring and alarm functions',
        'Test automatic isolation and suppression activation',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional test of thermal monitoring',
        'Check sensor positioning and condition',
        'Verify integration with fire suppression systems',
        'Monitor thermal trends during normal operations'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2, Battery manufacturer requirements'
  },

  // COMPREHENSIVE RA-VERIFIED FFE GUIDANCE FROM DESKTOP FILES
  // All equipment IDs mapped to exact RA guidance with WBC3 verification
  'ffe_extinguisher_control_position': {
    id: 'ffe_extinguisher_control_position',
    name: 'Control Position Fire Protection',
    equipment_type: 'control_position_extinguisher',
    
    placement: {
      requirements: [
        'Located within 2m from main control position (WBC3 16.3.1.3.1)',
        'Readily accessible during normal vessel operations',
        'Protected from damage but immediately available',
        'Positioned for safe access during control position emergencies'
      ]
    },
    
    specifications: {
      type: 'Multi-purpose portable fire extinguisher',
      standards: 'Portable fire extinguisher per WBC3 16.3.1.3.1',
      trigger: 'Universal requirement for all seagoing vessels',
      distance_requirement: 'Within 2 metres from main control position',
      quantity: 'One extinguisher minimum within specified distance',
      universal_requirement: 'Required regardless of other engine protection arrangements',
      multi_purpose: 'Suitable for electrical and general fires at control position'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify distance remains within 2m of control position',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check accessibility during normal vessel operations',
        'Ensure crew familiar with control position extinguisher location',
        'Verify extinguisher type suitable for control position equipment',
        'Test access from normal control positions'
      ]
    },
    
    authority: 'WBC3 16.3.1.3.1'
  },

  'ffe_extinguisher_cooking_flame': {
    id: 'ffe_extinguisher_cooking_flame',
    name: 'Portable Fire Extinguisher - 5A/34B (Galley - Base Requirement)',
    equipment_type: 'fire_extinguisher',
    specifications: {
      type: 'One 5A/34B extinguisher AND (one 8A/68B extinguisher OR fire blanket)',
      standards: 'BS EN 3 extinguishers for open flame cooking/heating',
      trigger: 'Cooking or heating appliances with open flame (gas, alcohol, solid fuel)'
    },
    maintenance: {
      mandatory: [
        'Annual inspection of all fire protection equipment',
        'Monthly visual inspection of extinguishers and blanket'
      ]
    },
    authority: 'WBC3 Table 16.2.3.1'
  },

  'ffe_fire_pump_required': {
    id: 'ffe_fire_pump_required',
    name: 'Fire Pump',
    equipment_type: 'fire_pump',
    specifications: {
      type: 'Power driven self-priming fire pump OR hand fire pump',
      standards: 'Fire pump per WBC3 16.4.2.1',
      trigger: 'All vessels except open boats <8m without substantial enclosure'
    },
    maintenance: {
      mandatory: [
        'Annual pump testing and inspection',
        'Verify sea connection integrity and flow'
      ]
    },
    authority: 'WBC3 16.4.2.1'
  },

  'ffe_fire_hose_fitted': {
    id: 'ffe_fire_hose_fitted',
    name: 'Fire Hose',
    equipment_type: 'fire_hose',
    specifications: {
      type: 'Fire hose of adequate length with 10mm nozzle and spray nozzle',
      standards: 'Fire hose per WBC3 16.4.2.4',
      trigger: 'Required when vessel has fire pump'
    },
    maintenance: {
      mandatory: [
        'Annual hose pressure testing and inspection',
        'Check nozzle operation and spray patterns'
      ]
    },
    authority: 'WBC3 16.4.2.4'
  },

  'ffe_plan_fire_control': {
    id: 'ffe_plan_fire_control',
    name: 'Fire Control and Safety Plan',
    equipment_type: 'fire_control_plan',
    specifications: {
      type: 'Fire control and safety plan showing equipment locations',
      standards: 'Fire control plan per WBC3 15.8.1',
      display_requirement: 'Prominently displayed at main control position'
    },
    maintenance: {
      mandatory: [
        'Annual review and update if vessel configuration changes',
        'Verify plan remains accurate for current equipment installation'
      ]
    },
    authority: 'WBC3 15.8.1'
  },

  'ffe_detector_fire_galley': {
    id: 'ffe_detector_fire_galley',
    name: 'Fire Detector - Galley',
    equipment_type: 'fire_detector',
    specifications: {
      type: 'Fire detectors appropriate for galley and cooking area hazards',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'Galley areas with cooking or heating equipment'
    },
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person'
      ]
    },
    authority: 'WBC3 15.6.2.1, EN 54'
  },

  'ffe_detector_co_galley': {
    id: 'ffe_detector_co_galley',
    name: 'CO Detector - Galley',
    equipment_type: 'co_detector',
    specifications: {
      type: 'Carbon monoxide detector for galley area',
      standards: 'CO detector per manufacturer instructions (WBC3 15.6.3.1)',
      trigger: 'Open flame, gas, or liquid fuel appliances in/adjacent to accommodation'
    },
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person',
        'Battery replacement per manufacturer schedule'
      ]
    },
    authority: 'WBC3 15.6.3.1'
  },

  // ACTUAL EQUIPMENT IDs FROM VESSEL REQUIREMENTS SYSTEM
  // Format: FFE_v2_xxx maps to ffe.xxx guidance but we need exact ID matches
  
  // FFE Equipment with full prefixed IDs
  'FFE_v2_ffe.extinguisher.outboard.le220kw': {
    id: 'FFE_v2_ffe.extinguisher.outboard.le220kw',
    name: 'Fire Extinguisher - Outboard ≤220kW',
    equipment_type: 'fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible for immediate use',
        'Protected from weather and corrosion',
        'Located where outboard engine fire can be safely attacked',
        'Positioned to avoid engine heat and vibration damage'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguisher rated 34B minimum',
      standards: 'BS EN 3 certified marine fire extinguisher (WBC3 Table 16.1.1.1)',
      trigger: 'Outboard engines with aggregate power ≤220kW',
      quantity: 'One 34B rated extinguisher minimum',
      rating: '34B minimum fire rating for Class B (liquid fuel) fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Replace when pressure drops or damage occurs',
        'Service per manufacturer recommendations'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  'FFE_v2_ffe.extinguisher.outboard.gt220kw': {
    id: 'FFE_v2_ffe.extinguisher.outboard.gt220kw', 
    name: 'Fire Extinguisher - Outboard >220kW',
    equipment_type: 'fire_extinguisher',
    
    specifications: {
      type: 'Multiple portable fire extinguishers with total B rating = 0.3P',
      standards: 'BS EN 3 certified marine fire extinguishers (WBC3 Table 16.1.1.1)',
      calculation: 'Total B capacity = 0.3 × P (where P = aggregate power in kW)'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing of all extinguishers',
        'Monthly visual inspection of pressure and condition',
        'Maintain total B rating by replacing expired units'
      ]
    },
    
    authority: 'WBC3 Table 16.1.1.1'
  },

  'FFE_v2_ffe.detector.machinery': {
    id: 'FFE_v2_ffe.detector.machinery',
    name: 'Fire Detection - Machinery Space', 
    equipment_type: 'fire_detector',
    
    specifications: {
      type: 'Fire detectors detecting smoke and heat, OR flame detectors',
      standards: 'EN 54 compliant fire detectors (WBC3 15.6.2.1)',
      trigger: 'All vessels with machinery space(s)'
    },
    
    maintenance: {
      mandatory: [
        'Maintained and tested per manufacturer recommendations (WBC3 15.6.1.1)',
        'Annual inspection and testing by competent person'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, EN 54'
  },

  'FFE_v2_ffe.fire.pump.required': {
    id: 'FFE_v2_ffe.fire.pump.required',
    name: 'Fire Pump',
    equipment_type: 'fire_pump',
    
    specifications: {
      type: 'Power driven self-priming fire pump OR hand fire pump',
      standards: 'Fire pump per WBC3 16.4.2.1',
      trigger: 'All vessels except open boats <8m without substantial enclosure'
    },
    
    maintenance: {
      mandatory: [
        'Annual pump testing and inspection',
        'Verify sea connection integrity and flow'
      ]
    },
    
    authority: 'WBC3 16.4.2.1'
  },

  // Fire Blanket (matches FFE_v2_ffe.fire.blanket)
  'FFE_v2_ffe_fire_blanket': {
    id: 'FFE_v2_ffe_fire_blanket',
    name: 'Fire Blanket',
    equipment_type: 'fire_blanket',
    
    placement: {
      requirements: [
        'Located in galley area with fire risk (cooking/heating with open flame)',
        'Readily accessible during cooking or heating operations',
        'Protected from contamination but immediately deployable',
        'Positioned for safe use during appliance emergencies'
      ]
    },
    
    specifications: {
      type: 'Fire blanket meeting appropriate standard',
      standards: 'BS EN 1869:2019 Fire Blankets (MIN 698)',
      trigger: [
        'Cooking or heating appliances with open flame',
        'Any area identified by CA as increased fire risk'
      ],
      locations: 'Galley or cooking area where fire risk identified',
      alternative: 'May be used instead of 8A/68B extinguisher for open flame protection',
      deployment: 'Must be readily deployable for smothering fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection by competent person',
        'Check blanket material integrity and fire resistance',
        'Verify deployment mechanism operation',
        'Replace if damaged or contaminated'
      ],
      recommended: [
        'Monthly visual inspection for damage or contamination',
        'Check accessibility during normal cooking operations',
        'Verify blanket deployment instructions remain clear',
        'Ensure crew trained on proper deployment technique'
      ]
    },
    
    authority: 'WBC3 16.5, 16.5.2, MIN 698'
  },

  // Fire Blanket (Galley) - Legacy compatibility
  'fire_blanket_galley': {
    id: 'fire_blanket_galley',
    name: 'Fire Blanket (Galley)',
    equipment_type: 'fire_blanket',
    
    placement: {
      requirements: [
        'Located in galley area with fire risk (cooking/heating with open flame)',
        'Readily accessible during cooking or heating operations',
        'Protected from contamination but immediately deployable',
        'Positioned for safe use during appliance emergencies'
      ]
    },
    
    specifications: {
      type: 'Fire blanket meeting appropriate standard',
      standards: 'BS EN 1869:2019 Fire Blankets (MIN 698)',
      trigger: [
        'Cooking or heating appliances with open flame',
        'Any area identified by CA as increased fire risk'
      ],
      locations: 'Galley or cooking area where fire risk identified',
      alternative: 'May be used instead of 8A/68B extinguisher for open flame protection',
      deployment: 'Must be readily deployable for smothering fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection by competent person',
        'Check blanket material integrity and fire resistance',
        'Verify deployment mechanism operation',
        'Replace if damaged or contaminated'
      ],
      recommended: [
        'Monthly visual inspection for damage or contamination',
        'Check accessibility during normal cooking operations',
        'Verify blanket deployment instructions remain clear',
        'Ensure crew trained on proper deployment technique'
      ]
    },
    
    authority: 'WBC3 16.5, 16.5.2, MIN 698'
  },

  // Fixed Fire Suppression System (Battery Compartment)
  'fixed_fire_suppression_system_battery_compartment': {
    id: 'fixed_fire_suppression_system_battery_compartment',
    name: 'Fixed Fire Suppression System (Battery Compartment)',
    equipment_type: 'battery_fire_suppression',
    
    placement: {
      requirements: [
        'Installed in battery boxes and battery rooms (WBC3 Annex 1 3.4.1)',
        'Per battery manufacturer installation requirements',
        'Protected from mechanical damage',
        'Accessible for maintenance and inspection'
      ]
    },
    
    specifications: {
      type: 'Fixed fire suppression system for battery spaces',
      standards: 'Per battery manufacturer requirements (WBC3 Annex 1 3.4.1)',
      trigger: 'All battery electric and hybrid propulsion vessels (mandatory)',
      manufacturer_compliance: 'Must accord with battery manufacturer requirements',
      coverage: 'Battery boxes and battery rooms',
      system_types: ['High pressure water mist (recommended)', 'Foam systems', 'Other manufacturer-approved systems'],
      installation: 'Per manufacturer installation requirements and WBC3 Annex 1'
    },
    
    maintenance: {
      mandatory: [
        'Service per manufacturer requirements (WBC3 Annex 1 3.4.1)',
        'Annual inspection by competent service provider',
        'Verify system compatibility with battery type',
        'Test automatic activation systems'
      ],
      recommended: [
        'Monthly visual inspection of system components',
        'Check control panel indications and alarms',
        'Verify manual activation mechanisms',
        'Coordinate with battery system maintenance schedules'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.4.1'
  },

  // Fire Detection System (Battery Compartment)
  'fire_detection_system_battery_compartment': {
    id: 'fire_detection_system_battery_compartment',
    name: 'Fire Detection System (Battery Compartment)',
    equipment_type: 'battery_fire_detection',
    
    placement: {
      requirements: [
        'Fitted in battery rooms, battery boxes, or spaces containing battery boxes (WBC3 Annex 1 3.2.1)',
        'Positioned to detect fires from battery cells and connections',
        'Auditory and visual alarms required (WBC3 Annex 1 3.2.1)',
        'Coverage of all battery installation areas'
      ]
    },
    
    specifications: {
      type: 'Smoke, heat, and flame detectors (all three types required)',
      standards: 'Suitable detectors per WBC3 Annex 1 Section 3.2.1',
      trigger: 'Battery electric or hybrid propulsion vessels',
      detection_types: [
        'Smoke detectors (required)',
        'Heat detectors (required)', 
        'Flame detectors (required)'
      ],
      alarm_requirements: [
        'Auditory alarms (WBC3 Annex 1 3.2.1)',
        'Visual alarms (WBC3 Annex 1 3.2.1)'
      ],
      coverage: 'Battery rooms, battery boxes, and spaces containing battery boxes'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Verify all three detection types (smoke, heat, flame) operational',
        'Test auditory and visual alarm functions',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional testing of detection systems',
        'Check detector positioning covers all battery areas',
        'Verify alarm audibility and visibility during battery operations',
        'Coordinate testing with battery system maintenance'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2.1'
  },

  // Portable Fire Extinguisher - 2x 34B (Battery Area)
  'portable_fire_extinguisher_2x_34b_battery_area': {
    id: 'portable_fire_extinguisher_2x_34b_battery_area',
    name: 'Portable Fire Extinguisher - 2x 34B (Battery Area)',
    equipment_type: 'battery_fire_extinguisher',
    
    placement: {
      requirements: [
        'Readily accessible to battery spaces (WBC3 Annex 1 3.5.2)',
        'Protected from weather and contamination',
        'Located for safe approach during battery fire',
        'Positioned near battery room/box entrances'
      ]
    },
    
    specifications: {
      type: 'Portable fire extinguishers suitable for battery fires',
      standards: 'Minimum 34B rating per WBC3 Annex 1 3.5.2',
      trigger: 'Battery electric and hybrid propulsion vessels',
      quantity: 'Minimum two extinguishers with 34B rating',
      additional_requirement: 'In addition to Section 16 fire protection requirements',
      manufacturer_type: 'Per battery manufacturer type requirements (WBC3 Annex 1 3.5.2)',
      rating: 'Minimum 34B fire rating for each extinguisher'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly visual inspection of pressure gauge and condition',
        'Verify extinguisher type suitable for battery fires',
        'Service per manufacturer recommendations'
      ],
      recommended: [
        'Check mounting security and accessibility monthly',
        'Ensure crew trained on battery fire suppression techniques',
        'Verify extinguisher compatibility with battery type',
        'Test accessibility during normal battery operations'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.5.2'
  },

  // Thermal Runaway Protection (Battery System)
  'thermal_runaway_protection_battery_system': {
    id: 'thermal_runaway_protection_battery_system',
    name: 'Thermal Runaway Protection (Battery System)',
    equipment_type: 'battery_thermal_protection',
    
    placement: {
      requirements: [
        'Integrated with battery management system',
        'Monitoring all battery cells and modules',
        'Connected to fire suppression and alarm systems',
        'Accessible for maintenance and testing'
      ]
    },
    
    specifications: {
      type: 'Thermal runaway detection and protection system',
      standards: 'Per battery manufacturer requirements (WBC3 Annex 1)',
      trigger: 'Battery electric and hybrid propulsion vessels with lithium batteries',
      monitoring: 'Continuous temperature and thermal gradient monitoring',
      response: 'Automatic system isolation and fire suppression activation',
      integration: 'Must integrate with battery management system and fire protection'
    },
    
    maintenance: {
      mandatory: [
        'Annual testing and calibration by competent person',
        'Verify thermal monitoring and alarm functions',
        'Test automatic isolation and suppression activation',
        'Maintain per manufacturer recommendations'
      ],
      recommended: [
        'Monthly functional test of thermal monitoring',
        'Check sensor positioning and condition',
        'Verify integration with fire suppression systems',
        'Monitor thermal trends during normal operations'
      ]
    },
    
    authority: 'WBC3 Annex 1 Section 3.2, Battery manufacturer requirements'
  },

  // LEGACY GUIDANCE (Backward compatibility)
  'ffe_extinguisher_accommodation': {
    id: 'ffe_extinguisher_accommodation',
    name: 'Accommodation Space Fire Extinguisher',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3',
    description: 'Fire extinguisher for accommodation spaces including wheelhouse.',
    authority: 'WBC3 Section 16.3.1.1',
    placement: {
      requirements: [
        'Required for accommodation spaces (WBC3 Section 16.3.1.1)',
        'Wheelhouse classified as accommodation space',
        'Must be accessible within 2 metres of control position'
      ]
    },
    specifications: {
      type: 'Multi-purpose dry powder or foam extinguisher',
      standards: 'WBC3 Table 16.1.1.1 compliance required',
      mounting: 'Readily accessible location'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pressure gauge monitoring',
        'Easy access for emergency use'
      ]
    }
  },

  'ffe_extinguisher_control': {
    id: 'ffe_extinguisher_control',
    name: 'Control Position Fire Extinguisher',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3.1.3.1',
    description: 'Fire extinguisher within 2 metres of control position.',
    authority: 'WBC3 Section 16.3.1.3.1',
    placement: {
      requirements: [
        'Within 2 metres of control position (WBC3 Section 16.3.1.3.1)',
        'Universal requirement for all commercial vessels',
        'Must be readily accessible to helmsman'
      ]
    },
    specifications: {
      type: 'Multi-purpose dry powder or foam extinguisher',
      standards: 'WBC3 compliance required',
      mounting: 'Within 2 metres of control position'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pressure gauge monitoring',
        'Immediate accessibility verification'
      ]
    }
  },

  'ffe_extinguisher_cooking_noflame': {
    id: 'ffe_extinguisher_cooking_noflame',
    name: 'Cooking Area Fire Extinguisher (No Open Flame)',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3',
    description: 'Fire extinguisher for cooking areas without open flame protection.',
    authority: 'WBC3 Section 16.3',
    placement: {
      requirements: [
        'Required for cooking areas without open flame protection',
        'Galley and cooking space protection',
        'Must be accessible from cooking position'
      ]
    },
    specifications: {
      type: 'Suitable for cooking fires (Class K or multi-purpose)',
      standards: 'WBC3 compliance required',
      mounting: 'Near cooking area but safe from heat'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pressure gauge monitoring',
        'Heat exposure assessment'
      ]
    }
  },

  'ffe_extinguisher_engine_box': {
    id: 'ffe_extinguisher_engine_box',
    name: 'Engine Compartment Fire Extinguisher',
    category: 'FFE',
    regulation: 'WBC3 Table 16.1.1.1',
    description: 'Fire extinguisher for engine compartments based on engine configuration.',
    authority: 'WBC3 Table 16.1.1.1',
    placement: {
      requirements: [
        'Engine compartment fire protection (WBC3 Table 16.1.1.1)',
        'Required for inboard engines based on configuration',
        'Fixed or portable system based on installation'
      ]
    },
    specifications: {
      type: 'Appropriate for engine fires (CO2, foam, or dry powder)',
      standards: 'WBC3 Table 16.1.1.1 compliance required',
      mounting: 'Engine compartment or adjacent accessible location'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'System integrity verification',
        'Engine compartment access confirmation'
      ]
    }
  },

  'ffe_extinguisher_galley_additional': {
    id: 'ffe_extinguisher_galley_additional',
    name: 'Additional Galley Fire Extinguisher',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3',
    description: 'Additional fire extinguisher for galley areas with enhanced fire risk.',
    authority: 'WBC3 Section 16.3',
    placement: {
      requirements: [
        'Additional protection for galley areas',
        'Enhanced fire risk mitigation',
        'Must be accessible from galley position'
      ]
    },
    specifications: {
      type: 'Suitable for galley fires (Class K or multi-purpose)',
      standards: 'WBC3 compliance required',
      mounting: 'Galley area but protected from heat and grease'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pressure gauge monitoring',
        'Galley environment assessment'
      ]
    }
  },

  'ffe_extinguisher_galley_base': {
    id: 'ffe_extinguisher_galley_base',
    name: 'Base Galley Fire Extinguisher',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3',
    description: 'Primary fire extinguisher for galley and cooking areas.',
    authority: 'WBC3 Section 16.3',
    placement: {
      requirements: [
        'Primary galley fire protection',
        'Cooking area safety requirement',
        'Must be readily accessible to cook'
      ]
    },
    specifications: {
      type: 'Suitable for cooking fires (Class K or multi-purpose)',
      standards: 'WBC3 compliance required',
      mounting: 'Near galley but safe from cooking hazards'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pressure gauge monitoring',
        'Cooking area accessibility verification'
      ]
    }
  },

  'ffe_extinguisher_openboat_le8m': {
    id: 'ffe_extinguisher_openboat_le8m',
    name: 'Open Boat Fire Extinguisher (≤8m)',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3',
    description: 'Fire extinguisher for open boats 8 metres or less.',
    authority: 'WBC3 Section 16.3',
    placement: {
      requirements: [
        'Required for open boats ≤8m length',
        'Weather protected storage required',
        'Must be readily accessible'
      ]
    },
    specifications: {
      type: 'Weather-resistant multi-purpose extinguisher',
      standards: 'WBC3 compliance required',
      mounting: 'Weather protected but readily accessible'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Weather protection integrity check',
        'Marine environment corrosion assessment'
      ]
    }
  },

  'ffe_extinguisher_outboard_gt220kw': {
    id: 'ffe_extinguisher_outboard_gt220kw',
    name: 'Outboard Engine Fire Extinguisher (>220kW)',
    category: 'FFE',
    regulation: 'WBC3 Table 16.1.1.1',
    description: 'Fire extinguisher for high-power outboard engines over 220kW.',
    authority: 'WBC3 Table 16.1.1.1',
    placement: {
      requirements: [
        'Required for outboard engines >220kW',
        'Engine-specific fire protection',
        'Must be accessible near engine controls'
      ]
    },
    specifications: {
      type: 'Suitable for engine fires (CO2 or dry powder)',
      standards: 'WBC3 Table 16.1.1.1 compliance required',
      mounting: 'Near engine controls but protected from spray'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Marine environment protection check',
        'Engine accessibility verification'
      ]
    }
  },

  'ffe_extinguisher_outboard_le220kw': {
    id: 'ffe_extinguisher_outboard_le220kw',
    name: 'Outboard Engine Fire Extinguisher (≤220kW)',
    category: 'FFE',
    regulation: 'WBC3 Table 16.1.1.1',
    description: 'Fire extinguisher for outboard engines 220kW or less.',
    authority: 'WBC3 Table 16.1.1.1',
    placement: {
      requirements: [
        'Required for outboard engines ≤220kW',
        'Portable extinguisher requirement',
        'Must be readily accessible'
      ]
    },
    specifications: {
      type: 'Portable multi-purpose extinguisher',
      standards: 'WBC3 Table 16.1.1.1 compliance required',
      mounting: 'Accessible near engine controls'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pressure gauge monitoring',
        'Weather exposure assessment'
      ]
    }
  },

  'ffe_extinguisher_pilot_boat_no_petrol': {
    id: 'ffe_extinguisher_pilot_boat_no_petrol',
    name: 'Pilot Boat Fire Extinguisher (Non-Petrol)',
    category: 'FFE',
    regulation: 'WBC3 Section 27.2.3',
    description: 'Fire extinguisher for pilot boats with non-petrol engines.',
    authority: 'WBC3 Section 27.2.3',
    placement: {
      requirements: [
        'Pilot boat specific requirements (WBC3 Section 27.2.3)',
        'Non-petrol engine configuration',
        'Enhanced accessibility for pilot operations'
      ]
    },
    specifications: {
      type: 'Pilot boat approved extinguisher',
      standards: 'WBC3 Section 27.2.3 compliance required',
      mounting: 'Readily accessible during pilot operations'
    },
    maintenance: {
      mandatory: [
        'Annual service and inspection required',
        'Monthly visual inspection',
        'Pilot operation accessibility check',
        'Marine environment assessment'
      ]
    }
  },

  // Fire Detection
  'ffe_detector_fire': {
    id: 'ffe_detector_fire',
    name: 'Fire Detection System',
    category: 'FFE',
    regulation: 'WBC3 Section 16.4',
    description: 'Fire detection system for engine compartments and enclosed spaces.',
    authority: 'WBC3 Section 16.4',
    placement: {
      requirements: [
        'Engine compartment detection required',
        'Enclosed space monitoring',
        'Visual and audible alarms required'
      ]
    },
    specifications: {
      type: 'Fire detection system with alarms',
      standards: 'WBC3 Section 16.4 compliance required',
      mounting: 'Engine compartment and control position'
    },
    maintenance: {
      mandatory: [
        'Annual system test and calibration',
        'Monthly functional test',
        'Alarm operation verification',
        'Sensor cleaning and inspection'
      ]
    }
  },

  // Fire Suppression Systems
  'ffe_fire_buckets_fitted': {
    id: 'ffe_fire_buckets_fitted',
    name: 'Fire Buckets (Fitted)',
    category: 'FFE',
    regulation: 'WBC3 Section 16.3',
    description: 'Fire buckets for manual fire fighting and emergency water access.',
    authority: 'WBC3 Section 16.3',
    placement: {
      requirements: [
        'Manual fire fighting capability',
        'Emergency water access',
        'Must be readily accessible on deck'
      ]
    },
    specifications: {
      type: 'Fire buckets with lanyard',
      standards: 'WBC3 compliance required',
      mounting: 'Deck accessible location with secure stowage'
    },
    maintenance: {
      mandatory: [
        'Monthly inspection for damage',
        'Lanyard integrity check',
        'Stowage security verification',
        'Corrosion assessment'
      ]
    }
  },

  'ffe_fire_hose': {
    id: 'ffe_fire_hose',
    name: 'Fire Hose System',
    category: 'FFE',
    regulation: 'WBC3 Section 16.5',
    description: 'Fire hose system for manual fire fighting operations.',
    authority: 'WBC3 Section 16.5',
    placement: {
      requirements: [
        'Manual fire fighting system',
        'Water supply connection required',
        'Must reach all areas of vessel'
      ]
    },
    specifications: {
      type: 'Fire hose with nozzle and connections',
      standards: 'WBC3 Section 16.5 compliance required',
      mounting: 'Accessible fire station with water supply'
    },
    maintenance: {
      mandatory: [
        'Annual pressure test',
        'Monthly visual inspection',
        'Nozzle operation test',
        'Connection integrity check'
      ]
    }
  },

  'ffe_fire_pump': {
    id: 'ffe_fire_pump',
    name: 'Fire Pump System',
    category: 'FFE',
    regulation: 'WBC3 Section 16.5',
    description: 'Fire pump system for pressurized fire fighting water supply.',
    authority: 'WBC3 Section 16.5',
    placement: {
      requirements: [
        'Pressurized water supply for fire fighting',
        'Independent power source preferred',
        'Must supply fire hose system'
      ]
    },
    specifications: {
      type: 'Fire pump with pressure system',
      standards: 'WBC3 Section 16.5 compliance required',
      mounting: 'Protected location with sea water access'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Annual service and inspection',
        'Pressure system verification',
        'Sea water intake inspection'
      ]
    }
  },

  'ffe_fire_pump_dangerous_goods': {
    id: 'ffe_fire_pump_dangerous_goods',
    name: 'Fire Pump (Dangerous Goods Operations)',
    category: 'FFE',
    regulation: 'WBC3 Dangerous Goods Requirements',
    description: 'Enhanced fire pump system for dangerous goods operations.',
    authority: 'WBC3 Dangerous Goods Requirements',
    placement: {
      requirements: [
        'Enhanced fire fighting for dangerous goods',
        'Higher capacity requirements',
        'Must meet dangerous goods fire protection standards'
      ]
    },
    specifications: {
      type: 'Enhanced capacity fire pump system',
      standards: 'WBC3 dangerous goods compliance required',
      mounting: 'Protected location with enhanced water supply'
    },
    maintenance: {
      mandatory: [
        'Monthly operation test',
        'Annual enhanced service inspection',
        'Dangerous goods compliance verification',
        'Enhanced pressure system testing'
      ]
    }
  },

  // EQUIPMENT REGISTER SPECIFIC NAMES (exact names from Equipment Register debug output)
  
  // Fire Detector - Sleeping Quarters (from Equipment Register debug output)
  'Fire Detector - Sleeping Quarters': {
    id: 'Fire Detector - Sleeping Quarters',
    name: 'Fire Detector - Sleeping Quarters',
    category: 'FFE',
    regulation: 'WBC3 Section 16.1.4',
    description: 'Fire detection system for sleeping accommodation areas.',
    authority: 'WBC3 Section 16.1.4',
    placement: {
      requirements: [
        'Early fire detection in sleeping quarters',
        'Suitable for accommodation space environment',
        'Audible alarm activation throughout vessel',
        'Integration with general alarm system'
      ]
    },
    specifications: {
      type: 'Smoke or heat detector appropriate for sleeping quarters',
      standards: 'WBC3 Section 16.1.4 fire detection requirements',
      installation: 'Ceiling mounted with optimal coverage',
      connectivity: 'Connected to vessel alarm system'
    },
    maintenance: {
      mandatory: [
        'Monthly functional test',
        'Annual sensitivity and calibration check',
        'Clean detector heads quarterly',
        'Test alarm integration annually'
      ]
    }
  },

  // Fire Detector - Wheelhouse (from Equipment Register debug output)
  'Fire Detector - Wheelhouse': {
    id: 'Fire Detector - Wheelhouse',
    name: 'Fire Detector - Wheelhouse',
    category: 'FFE',
    regulation: 'WBC3 Section 16.1.4',
    description: 'Fire detection system for wheelhouse and control areas.',
    authority: 'WBC3 Section 16.1.4',
    placement: {
      requirements: [
        'Early fire detection in control areas',
        'Protection of navigation equipment',
        'Immediate alarm to crew',
        'Integration with vessel systems'
      ]
    },
    specifications: {
      type: 'Smoke detector suitable for electronics environment',
      standards: 'WBC3 Section 16.1.4 fire detection requirements',
      installation: 'Positioned to avoid false alarms from equipment',
      connectivity: 'Integrated with navigation and alarm systems'
    },
    maintenance: {
      mandatory: [
        'Monthly functional test',
        'Annual sensitivity check',
        'Clean detector heads quarterly',
        'Verify alarm system integration'
      ]
    }
  },

  // CO Detector - Accommodation Spaces (from Equipment Register debug output)
  'CO Detector - Accommodation Spaces': {
    id: 'CO Detector - Accommodation Spaces',
    name: 'CO Detector - Accommodation Spaces',
    category: 'FFE',
    regulation: 'WBC3 Section 16.1.5',
    description: 'Carbon monoxide detector for accommodation and crew spaces.',
    authority: 'WBC3 Section 16.1.5',
    placement: {
      requirements: [
        'CO detection in crew accommodation',
        'Protection from engine exhaust infiltration',
        'Audible and visual alarm indication',
        'Coverage of all sleeping and living areas'
      ]
    },
    specifications: {
      type: 'Carbon monoxide detector with alarm',
      standards: 'WBC3 Section 16.1.5 CO detection requirements',
      sensitivity: 'Appropriate sensitivity for marine accommodation',
      alarm: 'Audible alarm with visual indication'
    },
    maintenance: {
      mandatory: [
        'Monthly functional test',
        'Annual calibration and sensor check',
        'Verify alarm levels and response',
        'Test in all accommodation spaces'
      ]
    }
  },

  // Missing IDs identified by audit - from RA verified guidance
  
  'ffe_fire_blanket_risk_area': {
    id: 'ffe_fire_blanket_risk_area',
    name: 'Fire Blanket',
    equipment_type: 'fire_blanket',
    
    placement: {
      requirements: [
        'Located near open flame cooking/heating appliance',
        'Both extinguisher and blanket readily accessible',
        'Protected from heat but immediately available',
        'Positioned for safe use during flame emergencies'
      ]
    },
    
    specifications: {
      type: 'Fire blanket for open flame protection',
      standards: 'Approved fire blanket per WBC3 Table 16.2.3.1',
      trigger: 'Cooking or heating appliances with open flame (gas, alcohol, solid fuel)',
      usage: 'Alternative to 8A/68B extinguisher for open flame areas',
      material: 'Fire resistant material suitable for galley/open flame use'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection of fire blanket condition',
        'Monthly visual check for damage or contamination',
        'Verify fire blanket not damaged or contaminated',
        'Check deployment mechanism operation'
      ],
      recommended: [
        'Check accessibility during cooking operations',
        'Ensure crew trained on fire blanket deployment',
        'Verify fire blanket deployment mechanism',
        'Practice emergency deployment procedures'
      ]
    },
    
    authority: 'WBC3 Table 16.2.3.1'
  },

  'ffe_detector_fire_sleeping': {
    id: 'ffe_detector_fire_sleeping',
    name: 'Fire Detector - Sleeping Quarters',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in sleeping quarters spaces',
        'Appropriate type for sleeping area hazards',
        'Positioned to detect fires from bedding and personal items',
        'Audible alarm capability to wake sleeping occupants'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for sleeping quarter hazards',
      standards: 'EN 54 compliant fire detectors',
      trigger: 'Sleeping accommodation spaces',
      detection_type: 'Typically smoke detection for bedding and personal items',
      alarm_requirements: 'Sufficient audibility to wake sleeping occupants'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection and testing by competent person',
        'Monthly functional testing of detectors and alarms',
        'Verify alarm audibility in sleeping areas',
        'Test detection response for typical sleeping quarter hazards'
      ],
      recommended: [
        'Check detector positioning for optimal sleeping quarter coverage',
        'Verify alarms wake sleeping occupants',
        'Test alarm audibility with doors closed',
        'Ensure detection covers all sleeping areas'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  'ffe_detector_fire_wheelhouse': {
    id: 'ffe_detector_fire_wheelhouse',
    name: 'Fire Detector - Wheelhouse',
    equipment_type: 'fire_detector',
    
    placement: {
      requirements: [
        'Fitted in wheelhouse/control areas',
        'Appropriate type for wheelhouse equipment hazards',
        'Positioned to detect fires from navigation and control equipment',
        'Audible alarm at wheelhouse and accommodation areas'
      ]
    },
    
    specifications: {
      type: 'Fire detectors appropriate for wheelhouse/control equipment',
      standards: 'EN 54 compliant fire detectors',
      trigger: 'Wheelhouse and control areas with navigation/control equipment',
      equipment_types: [
        'Navigation equipment (radar, GPS, plotters)',
        'Control equipment (engine controls, steering)',
        'Communication equipment (VHF, electronic systems)'
      ],
      detector_selection: 'Type appropriate for electrical and electronic equipment fires'
    },
    
    maintenance: {
      mandatory: [
        'Annual inspection considering wheelhouse equipment fire risks',
        'Monthly functional testing during normal operations',
        'Verify detection appropriate for wheelhouse equipment present',
        'Test alarm audibility in wheelhouse and accommodation'
      ],
      recommended: [
        'Check detector positioning relative to navigation equipment',
        'Verify detection covers all control and navigation areas',
        'Test alarm response during normal wheelhouse operations',
        'Ensure crew trained on wheelhouse fire procedures'
      ]
    },
    
    authority: 'WBC3 15.6.2.1, 15.6.2.2, 15.6.2.3, EN 54'
  },

  'ffe_detector_co_exhaust_primary': {
    id: 'ffe_detector_co_exhaust_primary',
    name: 'CO Detector - Exhaust Primary',
    equipment_type: 'gas_detector',
    
    placement: {
      requirements: [
        'Fitted where engine exhaust may enter accommodation or working areas',
        'Primary CO detection for exhaust infiltration protection',
        'Positioned to detect CO from engine exhaust systems',
        'Audible alarm capability in affected areas'
      ]
    },
    
    specifications: {
      type: 'Carbon monoxide detector for exhaust protection',
      standards: 'CO detector appropriate for marine exhaust detection',
      trigger: 'Areas where engine exhaust may infiltrate accommodation or crew areas',
      detection_level: 'Appropriate sensitivity for CO exhaust detection',
      alarm_capability: 'Audible and visual alarm indication'
    },
    
    maintenance: {
      mandatory: [
        'Annual calibration and sensitivity verification',
        'Monthly functional test and alarm verification',
        'Verify detection levels appropriate for CO protection',
        'Test alarm response and crew notification'
      ],
      recommended: [
        'Check detector positioning relative to potential exhaust infiltration',
        'Verify detection covers areas where crew may be exposed',
        'Test alarm audibility in all affected areas',
        'Review exhaust system integrity to prevent CO infiltration'
      ]
    },
    
    authority: 'WBC3 CO detection requirements for exhaust protection'
  }
};

/**
 * Simple Direct Lookup - No mappings, no complexity
 * @param {string} itemId - Equipment requirement ID
 * @returns {Object|null} Guidance object or null
 */
export function getFFESimpleGuidance(itemId) {
  return FFE_SIMPLE_GUIDANCE[itemId] || null;
}